// SearchActionsPage.tsx - Complete reference guide for all available search actions

import React, { useState } from 'react';
import { MagnifyingGlassIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { 
  searchActions, 
  categoryLabels, 
  SearchActionCategory,
  getActionsByCategory 
} from '../config/searchActions';
import { useSearch } from '../hooks/useSearch';

const SearchActionsPage: React.FC = () => {
  const { handleSearch } = useSearch();
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<SearchActionCategory | 'all'>('all');

  // Filter actions based on search and category
  const filteredActions = searchActions.filter(action => {
    const matchesSearch = searchFilter === '' || 
      action.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      action.description.toLowerCase().includes(searchFilter.toLowerCase()) ||
      action.keywords.some(kw => kw.toLowerCase().includes(searchFilter.toLowerCase())) ||
      action.examples.some(ex => ex.toLowerCase().includes(searchFilter.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || action.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const categories: SearchActionCategory[] = ['questions', 'generation', 'navigation', 'utilities'];

  const handleExampleClick = (example: string) => {
    handleSearch(example);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 mb-4">
            <SparklesIcon className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Search Actions Guide</h1>
          </div>
          <p className="text-muted-foreground mb-6">
            Learn what you can ask Edyt. Click any example to try it instantly.
          </p>

          {/* Search Filter */}
          <div className="relative max-w-2xl">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search actions, examples, or keywords..."
              className="block w-full rounded-md border border-input bg-background py-2 pl-10 pr-10 text-sm placeholder-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {searchFilter && (
              <button
                onClick={() => setSearchFilter('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <XMarkIcon className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
              }`}
            >
              All Actions ({searchActions.length})
            </button>
            {categories.map((cat) => {
              const count = getActionsByCategory(cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                >
                  {categoryLabels[cat]} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Actions List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredActions.length === 0 ? (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium text-foreground">No actions found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Try adjusting your search filter or category selection
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {categories.map((category) => {
              const categoryActions = filteredActions.filter(
                action => action.category === category
              );
              
              if (categoryActions.length === 0) return null;
              
              return (
                <div key={category} className="space-y-4">
                  {/* Category Header */}
                  <div className="flex items-center gap-3 pb-2 border-b border-border">
                    <h2 className="text-xl font-semibold text-foreground">
                      {categoryLabels[category]}
                    </h2>
                    <span className="text-sm text-muted-foreground">
                      {categoryActions.length} {categoryActions.length === 1 ? 'action' : 'actions'}
                    </span>
                  </div>

                  {/* Actions in Category */}
                  <div className="grid gap-4 md:grid-cols-2">
                    {categoryActions.map((action) => (
                      <div
                        key={action.id}
                        className="bg-card border border-border rounded-lg p-5 hover:border-primary/50 transition-colors"
                      >
                        {/* Action Header */}
                        <div className="flex items-start gap-3 mb-3">
                          <action.icon className="h-6 w-6 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-foreground mb-1">
                              {action.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {action.detailedDescription}
                            </p>
                          </div>
                        </div>

                        {/* Trigger Keywords */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                            Trigger Keywords
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {action.keywords.slice(0, 5).map((keyword, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground"
                              >
                                {keyword}
                              </span>
                            ))}
                            {action.keywords.length > 5 && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs text-muted-foreground">
                                +{action.keywords.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Examples */}
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                            Example Queries
                          </p>
                          <div className="space-y-1.5">
                            {action.examples.map((example, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleExampleClick(example)}
                                className="block w-full text-left px-3 py-2 rounded-md bg-muted/50 hover:bg-primary/10 hover:border-primary/50 border border-transparent text-sm text-foreground transition-colors group"
                              >
                                <span className="font-mono text-xs group-hover:text-primary transition-colors">
                                  {example}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Additional Info */}
                        {action.requiresCustomer && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              Requires customer name in query
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchActionsPage;
